import { Link, useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'redux/store';
import { getOwners } from 'redux/slices/Owners';
import { useEffect, useState, useRef } from 'react';
import useToggle from 'hooks/useToggle';
import { FormInput, Title, Spinner, TooltipIcon, Modal } from 'components';
import { Card, Col, Row, Form, Button, Alert, Accordion } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { cleanAccountDetails, getRootStoreGroups, getAccountsTableAdmin } from 'redux/slices/Accounts';
import { getPermissionNames } from 'redux/slices/Permissions';
import { childLevel, permissionsMenu } from 'pages/admin/NewAccount';
import { adminAdd, adminSearch, adminUpdate } from 'redux/slices/Accounts';
import { AdminUser } from 'models/dbModels/users/User';
import { getAccessFromEnum, UserAccess } from 'models/freedomModels/enums/userAccess';

export function NewUser() {
	const form = useRef(null);
	const { userId } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [isValidated, setValidated] = useToggle();
	const [loading, setLoading] = useState<boolean>(false);
	const owners = useAppSelector((state: RootState) => state.owners);
	const account = useAppSelector((state: RootState) => state.account);
	const [isNewUser, setIsNewUser] = useState<boolean>(false);
	const accounts = useAppSelector((state: RootState) => state.account);
	const [userPermissions, setUserPermissions] = useState(permissionsMenu);
	const [showPrompt, setShowPrompt] = useState<boolean>(false);

	useEffect(() => {
		dispatch(getRootStoreGroups());
		dispatch(getOwners());
		if (userId) {
			getAccount(userId);
			setIsNewUser(false);


			dispatch(getPermissionNames()).then(res => {
				let temp = [...userPermissions];
				let others: childLevel[] = [];
	
				let flatMap = temp.flatMap(i => i.children);
	
	
				res.data.forEach((permissionName: string) => {
					if (flatMap.find(i => i.key.toLowerCase() === permissionName.toLowerCase())) {
						//All good, we have it already
					} else {
						others.push({
							title: permissionName,
							key: permissionName.charAt(0).toLowerCase() + permissionName.slice(1),
							display: ['View', "Create", "Update", "Delete", "Import"],
							disabled: []
						})
					}
				});
	
				// Uncomment during development
				// if (others.length > 0) {
				// 	temp.push({
				// 		title: "Other", children: [...others]
				// 	});
				// }

				setUserPermissions(temp);
			});
			

		} else {
			dispatch(cleanAccountDetails());
			setIsNewUser(true);
			CheckAllActive();
		}
	}, [userId]);

	function getAccount(userId: string) {
		dispatch(adminSearch({ ids: [parseInt(userId)] }))
			.then((result) => {
				if (result.data.length === 1) {
					if (result.data[0].status === null) {
						result.data[0].status = "Active";
					}
				}
			}).catch((error) => {
				console.log(error);
			});
	}

	function createAccount() {
		setLoading(true);
		const payload: AdminUser = {
			id: 0,
			givenName: account.givenName,
			familyName: account.familyName,
			email: account.email,
			ownerId: account.ownerId,
			permissions: account.permissions,
			storeGroupId: account.storeGroupId,
			status: "Active",
			access: account.access,
			comments: account.comments
		}
		setTimeout(() => {
			dispatch(adminAdd([payload]))
				.then((result) => {
					dispatch(getAccountsTableAdmin());
					navigate('/support/users');
				}).catch((error) => {
					console.log(error);
				});
			setLoading(false);
		}, 1000);
	}

	function updateAccount() {
		setLoading(true);
		const { password, confirmPassword, isPasswordChanged, ...rest } = account
		let payload: AdminUser;
		if (account.isPasswordChanged === false) {
			payload = rest
		} else {
			payload = account
		}
		setTimeout(() => {
			dispatch(adminUpdate([payload]))
				.then((result) => {
					if (result.data === true) {
						dispatch(getAccountsTableAdmin());
						navigate('/support/users');
					}
				}).catch((error) => {
					console.log(error);
				});
			setLoading(false);
		}, 1000);
	}

	function handleSubmit() {
		if (account.isPasswordChanged) {
			if (account.password !== account.confirmPassword) {
				alert('Password and Confirm Password must be the same');
			}
		}
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			if (!isNewUser) {
				updateAccount();
			} else {
				createAccount();
			}
		}
		setValidated();
	}

	function handleChange(option: any, optionValue: any) {
		dispatch({
			type: 'accountSlice/updateAccountField',
			payload: { key: option, value: optionValue }
		});
	}


	function getValue(key: string, type: string) {
		let permissions = account.permissions as any;
		if (permissions[key]) {
			return permissions[key][type] as boolean;
		}
		return false;
	}

	function setValue(key: string, type: string, checked: boolean) {
		//@ts-ignore
		let typeTemp = { ...account.permissions[key] }
		typeTemp[type] = checked;

		let temp: any = { ...account.permissions };
		temp[key] = typeTemp;

		handleChange("permissions", temp);
	}


	function setMultiValue(children: childLevel[], typeDisplay: string, type: string, checked: boolean) {

		let temp: any = { ...account.permissions };

		children.forEach(i => {
			if (i.display.includes(typeDisplay)) {
				//@ts-ignore
				let typeTemp = { ...account.permissions[i.key] }
				typeTemp[type] = checked;

				temp[i.key] = typeTemp;
			}
		})

		handleChange("permissions", temp);
	}

	function getMultiValue(children: childLevel[], typeDisplay: string, type: string): boolean {
		let filtered = children.filter(i => i.display.includes(typeDisplay));
		let checked = filtered.length > 0 ? filtered.every(c => getValue(c.key, type)) : false;

		return checked;
	}

	function CheckAllActive(option: 'Reset' | 'Uncheck' | 'Check' = 'Reset') {
		//New Account? Check all!
		let tempPermission: any = { ...account.permissions };

		let temp = [...userPermissions];
		temp.forEach(element => {
			element.children.forEach(child => {

				['canView', 'canCreate', 'canUpdate', 'canDelete', 'canImport'].forEach(prop => {
					//@ts-ignore
					let typeTemp = { ...tempPermission[child.key] }
					typeTemp[prop] = (option === 'Check' ? true : false);
					tempPermission[child.key] = typeTemp;
				})

				if (option === 'Reset') {
					child.display.forEach(display => {
						//@ts-ignore
						let typeTemp = { ...tempPermission[child.key] }
						typeTemp['can' + display] = true;
						tempPermission[child.key] = typeTemp;
					})
				}
			});
		});


		handleChange("permissions", tempPermission);
	}

	return (
		<>
			<Title pageTitle={isNewUser ? "New User" : "Update User"} parent="Admin" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Form validated={isValidated} ref={form}>
									<Row>
										<h4 className="mt-2">User Details</h4>
										<p className="text-muted">Fill the form below create a new account. User will be sent an email to create their password.</p>
										<Col md={6}>
											<FormInput
												label="First Name"
												type="text"
												name="account-given-name"
												placeholder="Enter first name"
												containerClass={'mb-3'}
												required
												value={account.givenName}
												onChange={(e) => handleChange("givenName", e.target.value)}
											/>
										</Col>
										<Col md={6}>
											<FormInput
												label="Last Name"
												type="text"
												name="account-family-name"
												placeholder="Enter last name"
												containerClass={'mb-3'}
												required
												value={account.familyName}
												onChange={(e) => handleChange("familyName", e.target.value)}
											/>
										</Col>
									</Row>
									<Row>
										<Col md={6}>
											<div className="form-group">
												<FormInput
													label="Email"
													type="email"
													name="account-email"
													placeholder="Enter email"
													containerClass={'mb-3'}
													required
													value={account.email}
													onChange={(e) => handleChange("email", e.target.value)}
												/>
											</div>
										</Col>
										<Col md={6}>
											<FormInput
												label="Comments"
												type="text"
												name="account-comment"
												placeholder="Enter comments"
												containerClass={'mb-3'}
												value={account.comments || ""}
												onChange={(e) => handleChange("comments", e.target.value )}
											/>
										</Col>
										{/* <Col md={6}>
											<Form.Group>
												<Form.Label className="form-check-label">Permissions</Form.Label>
												<Form.Select className="custom-select"
													required
													onChange={(e) => handleChange("permissionId", parseInt(e.target.value))}
													defaultValue={account.permissionId}
												>
													<option hidden value='' selected={account.permissionId.toString() === ""}>Choose a role</option>
													{permissions.table.map((permission: any, index: any) => {
														return <option key={index} value={permission.id.toString()} selected={account.permissionId.toString() === permission.id.toString()}>{permission.name}</option>
													})
													}
												</Form.Select>
											</Form.Group>
										</Col> */}
									</Row>
									<Row className="mt-2">
										<Col md={6} >
											<Form.Group>
												<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Store Group</Form.Label>
												<Form.Select className="custom-select"
													required
													onChange={(e) => {
														handleChange("storeGroupId", parseInt(e.target.value));

														const owner = accounts.rootStoreGroups.find((group) => group.id === parseInt(e.target.value));
														handleChange("ownerId", owner?.ownerId ?? 0);
													}}
													value={account.storeGroupId}
												>
													<option hidden value='0'>Choose a store group</option>
													{accounts.rootStoreGroups.map((storeGroup, index: number) => {
														return <option key={index} value={storeGroup.id}>{storeGroup.name}</option>
													})
													}
												</Form.Select>
											</Form.Group>
										</Col>

										<Col md={6} >
										<div className="form-group">
												<FormInput
													label="Owner"
													type="text"
													name="owner-name"
													placeholder="Select Store Group"
													containerClass={'mb-3'}
													value={owners.table.find((owner) => owner.id === account.ownerId)?.name ?? ""}
													disabled
												/>
											</div>
										</Col>
									</Row >
									<Row className="mt-3">
										<Col md={6} >
											{!isNewUser &&
												<Form.Group>
													<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Status</Form.Label>
													<div>
														<Form.Check
															type="switch"
															id="stock-active"
															label={account.status}
															checked={account.status === "Active" && true}
															onChange={(e) => {
																if (account.status === "Active") {
																	handleChange('status', "Suspended")
																} else {
																	handleChange('status', "Active")
																}
															}}
														/>
													</div>
												</Form.Group>
											}
										</Col>
										<Col md={6} >
											<Form.Group>
												<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Access</Form.Label>
												<Row>
													<Col md={3}>
														<Form.Check
															type="switch"
															id="zfreedom-enable"
															label="Z Freedom"
															checked={getAccessFromEnum(account.access).find(a => a === UserAccess.ZFreedom) != null ? true : false}
															onChange={(e) => handleChange("access", account.access ^ UserAccess.ZFreedom)}
														/>
													</Col>
													<Col md={3}>
														<Form.Check
															type="switch"
															id="zPocket-enable"
															label="Z Pocket"
															checked={getAccessFromEnum(account.access).find(a => a === UserAccess.ZPocket) != null ? true : false}
															onChange={(e) => handleChange("access", account.access ^ UserAccess.ZPocket)}
														/>
													</Col>
												</Row>
											</Form.Group>
										</Col>
									</Row >

									<Row>
										<Accordion>
											<Accordion.Item eventKey="0">
												<Accordion.Header>Permissions&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(<i className='uil-times-circle'></i>&nbsp;Indicates hidden from users)</Accordion.Header>
												<Accordion.Body>

													<div className='d-inline-flex flex-row align-items-center align-self-center mb-4'>
														<b>Settings</b>
														<Button className='mx-2' variant='outline-primary' onClick={() => CheckAllActive('Reset')}>Reset All Permissions</Button>
														<Button className='mx-2' variant='outline-primary' onClick={() => CheckAllActive('Check')}>Check All Permissions</Button>
														<Button className='mx-2' variant='outline-primary' onClick={() => CheckAllActive('Uncheck')}>Uncheck All Permissions</Button>
													</div>



													{userPermissions.map((p, i) => (
														<div key={i} className='pb-3'>
															{p.children.length > 0 && (
																<Row className='border pt-2 bg-body-tertiary '>
																	<Col md={2}>
																		<h4>{p.title}</h4>
																	</Col>

																	<Col md={2}>
																		<Form.Group hidden={!p.children.some(i => i.display.includes('View'))}>
																			<Form.Check type="checkbox" label="View" checked={getMultiValue(p.children, "View", 'canView')} onChange={(e) => setMultiValue(p.children, "View", "canView", e.currentTarget.checked)} />
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group hidden={!p.children.some(i => i.display.includes('Create'))}>
																			<Form.Check type="checkbox" label="Create" checked={getMultiValue(p.children, "Create", 'canCreate')} onChange={(e) => setMultiValue(p.children, "Create", "canCreate", e.currentTarget.checked)} />
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group hidden={!p.children.some(i => i.display.includes('Update'))}>
																			<Form.Check type="checkbox" label="Update" checked={getMultiValue(p.children, "Update", 'canUpdate')} onChange={(e) => setMultiValue(p.children, "Update", "canUpdate", e.currentTarget.checked)} />
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group hidden={!p.children.some(i => i.display.includes('Delete'))}>
																			<Form.Check type="checkbox" label="Delete" checked={getMultiValue(p.children, "Delete", 'canDelete')} onChange={(e) => setMultiValue(p.children, "Delete", "canDelete", e.currentTarget.checked)} />
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group hidden={!p.children.some(i => i.display.includes('Import'))}>
																			<Form.Check type="checkbox" label="Import" checked={getMultiValue(p.children, "Import", 'canImport')} onChange={(e) => setMultiValue(p.children, "Import", "canImport", e.currentTarget.checked)} />
																		</Form.Group>
																	</Col>
																</Row>
															)}
															{p.children.length > 0 && p.children.map((c, ii) => (
																<div key={ii}>
																	<Row className='my-1'>
																		<Col md={2}>
																			<div className="text-muted ms-1">{c.title}{c.tooltip && (<TooltipIcon text={c.tooltip} placement='right' />)}</div>
																		</Col>
																		<Col md={2}>
																			<Form.Group>
																				<Form.Check type="checkbox" label={<>View <i className='uil-times-circle' hidden={c.display.includes('View')}></i> </>} checked={getValue(c.key, 'canView')} onChange={(e) => setValue(c.key, 'canView', e.currentTarget.checked)} />
																			</Form.Group>
																		</Col>
																		<Col md={2}>
																			<Form.Group>
																				<Form.Check type="checkbox" label={<>Create <i className='uil-times-circle' hidden={c.display.includes('Create')}></i> </>} checked={getValue(c.key, 'canCreate')} onChange={(e) => setValue(c.key, 'canCreate', e.currentTarget.checked)} />
																			</Form.Group>
																		</Col>
																		<Col md={2}>
																			<Form.Group>
																				<Form.Check type="checkbox" label={<>Update <i className='uil-times-circle' hidden={c.display.includes('Update')}></i> </>} checked={getValue(c.key, 'canUpdate')} onChange={(e) => setValue(c.key, 'canUpdate', e.currentTarget.checked)} />
																			</Form.Group>
																		</Col>
																		<Col md={2}>
																			<Form.Group>
																				<Form.Check type="checkbox" label={<>Delete <i className='uil-times-circle' hidden={c.display.includes('Delete')}></i> </>} checked={getValue(c.key, 'canDelete')} onChange={(e) => setValue(c.key, 'canDelete', e.currentTarget.checked)} />
																			</Form.Group>
																		</Col>
																		<Col md={2}>
																			<Form.Group>
																				<Form.Check type="checkbox" label={<>Import <i className='uil-times-circle' hidden={c.display.includes('Import')}></i> </>} checked={getValue(c.key, 'canImport')} onChange={(e) => setValue(c.key, 'canImport', e.currentTarget.checked)} />
																			</Form.Group>
																		</Col>
																	</Row>

																</div>
															))}

														</div>
													))}
												</Accordion.Body>
											</Accordion.Item>
										</Accordion>

									</Row>
									<Row className='mt-3'>
										<Col sm={6}>
											<Link to="/support/users" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
												<i className="mdi mdi-arrow-left"></i> Back to All Users {' '}
											</Link>
										</Col>
										<Col sm={6} className="d-flex flex-row-reverse">
											<Button variant="primary" className="btn cta-button" disabled={loading} onClick={() => setShowPrompt(true)}>
												{loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
												{loading ? <>Saving...</> : <>Save</>}
											</Button>
										</Col>
									</Row>
								</Form>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Modal
				title="Confirm - Save User"
				show={showPrompt}
				buttonType="primary"
				disableButtons={false}
				buttonText={"Accept"}
				handleClose={() => setShowPrompt(false)}
				handleSubmit={() => {
					handleSubmit();
					setShowPrompt(false);
				}}
				description={
					<>
						<p>Data breaches can occur with the incorrect selection of stores/store groups.</p>
						<p>Please double-check your selection.</p>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>
		</>
	);
}